// External imports
import React, { useEffect } from 'react';
import moment from 'moment-timezone';
import {
  Button,
  Collapse,
  Divider,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  withStyles,
} from '@material-ui/core';
// Internal imports
import { useSetState } from '~/app/Utility/customHooks';
import ErrorHelpers from '~/app/errorHelpers.js';
import { statusOK } from '~/app/webHelpers.js';
import Spinner from '#/Common/Spinner.jsx';
import XlinkAPI from '~/app/api/xlinkAPI';
import {
  printList,
  formatPrintTimestamp,
} from '~/app/Pages/Returns/components/Print/printListHelpers.js';
import PrintListButton from '~/app/Pages/Returns/components/Print/PrintListButton.jsx';
import { LOAD_STATUS } from '~/app/constants.js';
// Style imports
import { styles } from '~/app/Pages/Returns/components/Events/eventLogStyles.js';

const EventLog = props => {
  const { classes } = props;
  const [state, setState] = useSetState({
    isAddingEvent: false,
    events: [],
    event: '',
    details: '',
    eventsStatus: LOAD_STATUS.PENDING,
  });

  useEffect(() => {
    getEventLogs();
  }, []);

  const getEventLogs = async () => {
    setState({ eventsStatus: LOAD_STATUS.PENDING });

    try {
      const res = await XlinkAPI.getEventLogs(props.returnID);
      if (statusOK(res, false)) {
        setState({
          events: Array.isArray(res.data) ? res.data : [],
          eventsStatus: LOAD_STATUS.LOADED,
        });
      } else {
        setState({ eventsStatus: LOAD_STATUS.FAILED });
        ErrorHelpers.handleError(
          'Error fetching event logs',
          ErrorHelpers.createSimpleError('Unable to load the event log. Please try again.'),
        );
      }
    } catch (err) {
      setState({ eventsStatus: LOAD_STATUS.FAILED });
      ErrorHelpers.handleError('Error fetching event logs', err);
    }
  };

  const submitEvent = async () => {
    try {
      const res = await XlinkAPI.insertEventLog(
        props.returnID,
        props.pssn,
        state.event,
        state.details,
      );
      if (statusOK(res, false)) {
        getEventLogs();
        toggleAddEventForm();
      }
    } catch (err) {
      ErrorHelpers.handleError('Error submitting event log', err);
    }
  };

  const handleChange = e => {
    setState({
      [e.target.name]: e.target.value,
    });
  };

  /**
   * Prints the event log as it is currently listed. Mirrors the desktop Print
   * button added to the Event Log dialog (1040/scr/cmdevt.scr :EVTPRINT).
   */
  const handlePrintEventLog = () => {
    printList({
      title: 'Event Log',
      columns: ['Event', 'Details', 'Login ID', 'Timestamp'],
      rows: state.events.map(event => [
        event.event,
        event.details,
        event.loginID,
        formatPrintTimestamp(event.date, true),
      ]),
      meta: props.printMeta,
      columnStyles: {
        0: { cellWidth: 45 },
        2: { cellWidth: 35 },
        3: { cellWidth: 45 },
      },
      emptyText: 'No event logs',
      filename: 'event_log.pdf',
    });
  };

  const toggleAddEventForm = () => {
    setState({
      isAddingEvent: !state.isAddingEvent,
      event: '',
      details: '',
    });
  };

  return (
    <div id="divEventLogModal">
      <Typography variant="h6" id="simple-modal-title" style={{ paddingLeft: '1em' }}>
        Event Log
      </Typography>
      <div className="eventLogModal">
        <Table>
          <TableHead>
            <TableRow classes={{ root: classes.tableHeadRoot }}>
              <TableCell>Event</TableCell>
              <TableCell>Details</TableCell>
              <TableCell>Login ID</TableCell>
              <TableCell>Timestamp</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {state.events.length
              ? state.events.map((event, i) => (
                  <TableRow classes={{ root: classes.tableRowRoot }} key={i}>
                    <TableCell
                      className="noselect"
                      component="th"
                      scope="row"
                      classes={{ root: classes.tableCellRoot }}
                    >
                      {event.event}
                    </TableCell>
                    <TableCell className="noselect" classes={{ root: classes.tableCellRoot }}>
                      {event.details}
                    </TableCell>
                    <TableCell className="noselect">{event.loginID}</TableCell>
                    <TableCell className="noselect">
                      {moment.utc(event.date).local().format('MM/DD/YYYY hh:mm:ss A')}
                    </TableCell>
                  </TableRow>
                ))
              : state.eventsStatus !== LOAD_STATUS.PENDING && (
                  <TableRow classes={{ root: classes.tableRowRoot }}>
                    <TableCell className="noselect" component="th" scope="row">
                      {state.eventsStatus === LOAD_STATUS.FAILED
                        ? 'Unable to load event logs'
                        : 'No event logs'}
                    </TableCell>
                    <TableCell className="noselect" />
                    <TableCell className="noselect" />
                    <TableCell className="noselect" />
                  </TableRow>
                )}
          </TableBody>
        </Table>
        {state.eventsStatus === LOAD_STATUS.PENDING ? (
          <Spinner
            size={100}
            color="blue"
            loadingText="Loading Events..."
            textColor="white"
            bgColor="grey"
            lockActions={true}
          />
        ) : null}
      </div>
      <Divider />
      <Collapse in={state.isAddingEvent} timeout="auto" unmountOnExit>
        <Grid
          container
          direction="column"
          justify="space-between"
          classes={{ container: classes.containerPadding }}
          spacing={32}
        >
          <Grid item xs={8}>
            <TextField
              id="eventText"
              name="event"
              label="Event"
              value={state.event}
              onChange={handleChange}
              inputProps={{ maxLength: '50' }}
              fullWidth
              variant="outlined"
              placeholder="Return Unlocked..."
              required
            />
          </Grid>

          <Grid item xs>
            <TextField
              id="detailsText"
              name="details"
              label="Details"
              value={state.details}
              onChange={handleChange}
              inputProps={{ maxLength: '300' }}
              fullWidth
              rows={4}
              multiline
              variant="outlined"
              placeholder="This return was unlocked..."
            />
          </Grid>
        </Grid>
      </Collapse>
      <Grid container justify="flex-end" classes={{ container: classes.containerPadding }}>
        <Grid item>
          {!state.isAddingEvent && (
            <PrintListButton
              id="btnEventLogPrint"
              style={{ marginRight: '1rem' }}
              variant="outlined"
              onClick={handlePrintEventLog}
              disabled={state.eventsStatus !== LOAD_STATUS.LOADED}
            />
          )}
          <Button
            id="btnEventLogCancel"
            color="primary"
            style={{ marginRight: '1rem' }}
            variant="outlined"
            onClick={() => (state.isAddingEvent ? toggleAddEventForm() : props.toggleModal(false))}
          >
            {state.isAddingEvent ? 'Cancel' : 'Close'}
          </Button>
          <Button
            id="btnEventLogAdd"
            color="primary"
            variant="contained"
            onClick={() => (state.isAddingEvent ? submitEvent() : toggleAddEventForm())}
            disabled={
              state.isAddingEvent
                ? props.locked || state.event === ''
                : props.locked || (props.readyForReview && props.isFeederOffice)
            }
          >
            {state.isAddingEvent ? 'Submit' : 'Add'}
          </Button>
        </Grid>
      </Grid>
    </div>
  );
};

export default withStyles(styles)(EventLog);
